import { Button, DialogDescription, DialogHeader, DialogTitle, Icon, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"

import type { PartnerSendResult } from "./use-multi-partner-send"

export interface SendPartnerResultsViewProps {
  results: readonly PartnerSendResult[]
  retrying: boolean
  onRetryFailed: () => void
  onViewOrder: (orderId: string) => void
  onDone: () => void
}

// 44px tall on touch screens, the compact 32px with a fine pointer.
const TOUCH_TARGET_CLASS = "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8"

const OUTCOME_ICON = {
  sent: { name: "check", className: "text-success" },
  pending: { name: "clock", className: "text-warning" },
  failed: { name: "alert-circle", className: "text-destructive" },
} as const

/** One row per partner after a multi-partner send, so a partial failure is never hidden. */
export function SendPartnerResultsView({
  results,
  retrying,
  onRetryFailed,
  onViewOrder,
  onDone,
}: SendPartnerResultsViewProps) {
  const { t } = useTranslation()
  const failedCount = results.filter((r) => r.outcome === "failed").length
  const allSent = results.every((r) => r.outcome === "sent")

  return (
    <div className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle className="text-base">
          {allSent
            ? t("marketplace.sendHandoff.partnerResults.titleAllSent", { count: results.length })
            : t("marketplace.sendHandoff.partnerResults.titlePartial")}
        </DialogTitle>
        <DialogDescription>
          {t("marketplace.sendHandoff.partnerResults.description")}
        </DialogDescription>
      </DialogHeader>

      <ul className="flex flex-col gap-2" aria-label={t("marketplace.sendHandoff.partnerResults.listLabel")}>
        {results.map((result) => {
          const count = result.send.scriptIds.length + result.send.briefIds.length
          return (
            <li
              key={result.send.connectionId}
              data-outcome={result.outcome}
              className="flex items-start gap-3 rounded-lg border border-border px-3 py-2.5"
            >
              <Icon
                name={OUTCOME_ICON[result.outcome].name}
                size="sm"
                className={cn("mt-0.5 shrink-0", OUTCOME_ICON[result.outcome].className)}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">{result.send.vendorName}</p>
                <p className="text-xs text-muted-foreground">
                  {result.outcome === "failed"
                    ? result.error
                    : result.outcome === "pending"
                      ? t("marketplace.sendHandoff.partnerResults.pending")
                      : t(
                          `marketplace.sendHandoff.partnerResults.sent_${count === 1 ? "one" : "other"}`,
                          { count },
                        )}
                </p>
              </div>
              {result.orderId ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className={TOUCH_TARGET_CLASS}
                  // Leaving mid-retry would unmount the dialog and stop the remaining partners.
                  disabled={retrying}
                  onClick={() => onViewOrder(result.orderId!)}
                >
                  {t("marketplace.sendHandoff.viewOrder")}
                </Button>
              ) : null}
            </li>
          )
        })}
      </ul>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button size="sm" variant="outline" className={TOUCH_TARGET_CLASS} onClick={onDone} disabled={retrying}>
          {t("common.close")}
        </Button>
        {failedCount > 0 ? (
          <Button
            size="sm"
            onClick={onRetryFailed}
            disabled={retrying}
            iconStart={retrying ? "loader" : "refresh-cw"}
            className={cn(TOUCH_TARGET_CLASS, retrying && "[&_svg]:animate-spin")}
          >
            {t(
              `marketplace.sendHandoff.partnerResults.retryFailed_${failedCount === 1 ? "one" : "other"}`,
              { count: failedCount },
            )}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
